import React, { useEffect } from 'react';
import { CheckCircle, Home, Shield, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const ConfirmSuccess: React.FC = () => {
  // Auto-close window after 30 seconds when on this page
  useEffect(() => {
    const timer = setTimeout(() => {
      window.close();
    }, 30000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex flex-col items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8">
        
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Document Receipt Confirmed!
          </h1>
          
          <p className="text-gray-600 text-base">
            Thank you for confirming that you received your printed document.
          </p>
        </div>

        {/* Security Features */}
        <div className="space-y-4 mb-8">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Trash2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-green-900">
                  Document Deleted
                </h3>
                <p className="text-sm text-green-700 mt-1">
                  Your document has been permanently deleted from our system to protect your privacy.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-900">
                  Privacy Protected
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  No copies of your document remain in our system. Your data is completely secure.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Summary */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 text-center">
            Your Prindox Journey
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">1</span>
              </div>
              <span className="text-sm text-gray-700">✅ Document uploaded securely</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">2</span>
              </div>
              <span className="text-sm text-gray-700">✅ Document printed at shop</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">3</span>
              </div>
              <span className="text-sm text-gray-700">✅ Receipt confirmed by you</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">4</span>
              </div>
              <span className="text-sm text-gray-700">✅ Document permanently deleted</span>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center space-y-4">
          <p className="text-sm text-gray-600">
            Need to print more documents? Find a nearby Prindox shop.
          </p>
          
          <Link
            to="/find-shops"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Home className="w-4 h-4" />
            <span>Find Nearby Shops</span>
          </Link>
          
          <p className="text-xs text-gray-500">
            This page will close automatically in 30 seconds
          </p>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <div className="flex items-center justify-center space-x-6 text-xs text-gray-400 mb-4">
            <div className="flex items-center space-x-1">
              <Shield className="w-3 h-3" />
              <span>End-to-end Security</span>
            </div>
            <div className="flex items-center space-x-1">
              <Trash2 className="w-3 h-3" />
              <span>Auto-Delete</span>
            </div>
            <div className="flex items-center space-x-1">
              <CheckCircle className="w-3 h-3" />
              <span>Verified Delivery</span>
            </div>
          </div>
          
          <p className="text-xs text-gray-500">
            Powered by <span className="font-semibold">Prindox</span> - 
            Secure Document Printing Network
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSuccess;